import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { useHistory, useLocation, useParams } from "react-router";
import SecoundaryHeader from "../Nav/SecoundaryHeader";
import { asyncWrap } from "../../utils/utils";

const fmt = new Intl.NumberFormat("en-IN");
const num = value => (Number.isFinite(Number(value)) ? Number(value) : 0);
const list = payload => payload?.data?.data || payload?.data || payload || [];
const pointsEarned = item => num(item.PointsEarned ?? item["Points Earned"]);
const schemeTitle = value => value || "Scheme Month Wise Analytics";
const currentMonthKey = () => {
  const now = new Date();
  return now.getFullYear() * 12 + now.getMonth() + 1;
};
const isTillCurrentMonth = item =>
  num(item.Year) * 12 + num(item.MonthNumber) <= currentMonthKey();

const styles = {
  page: { background: "#f5f7fb", minHeight: "100vh", padding: "86px 12px 24px" },
  shell: { margin: "0 auto", maxWidth: 920 },
  card: {
    background: "#fff",
    border: "1px solid #e8edf5",
    borderRadius: 8,
    boxShadow: "0 8px 22px rgba(31, 41, 55, 0.06)",
    marginBottom: 14,
    padding: 14,
  },
  titleRow: {
    alignItems: "center",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
    marginBottom: 10,
  },
  h2: { color: "#111827", fontSize: 18, fontWeight: 800, margin: 0 },
  hint: { color: "#6b7280", fontSize: 12, fontWeight: 600, margin: "3px 0 0" },
  alert: {
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    borderRadius: 8,
    color: "#9a3412",
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 12,
    padding: 10,
  },
  loader: { alignItems: "center", display: "flex", justifyContent: "center", minHeight: 300 },
  monthGrid: { display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(132px, 1fr))" },
  monthCard: { background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: 10 },
  monthName: { color: "#111827", fontSize: 13, fontWeight: 800, marginBottom: 8 },
  metricRow: { display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12, marginTop: 5 },
  metricLabel: { color: "#64748b", fontWeight: 700 },
  metricValue: { color: "#111827", fontWeight: 800, textAlign: "left" },
};

const SchemeMonthWiseAnalytics = () => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const title = schemeTitle(location.state?.schemeName);

  const fetchSchemeMonthWise = async () => {
    setLoading(true);
    setErrorMessage("");

    const [error, result] = await asyncWrap(
      axios.get(`/user-analytics-fy/scheme-analytics-month-wise/${id}`)
    );

    if (!result) {
      console.log(error);
      setErrorMessage("Unable to load scheme month wise analytics right now.");
      setMonthlyData([]);
      setLoading(false);
      return;
    }

    const monthList = Array.isArray(list(result?.data)) ? list(result?.data) : [];
    setMonthlyData(monthList.filter(isTillCurrentMonth));
    setLoading(false);
  };

  useEffect(() => {
    fetchSchemeMonthWise();
  }, [id]);

  const monthLabels = useMemo(
    () => monthlyData.map(item => `${String(item.Month || "").slice(0, 3)} ${item.Year || ""}`),
    [monthlyData]
  );

  const monthSeries = useMemo(
    () => [
      { name: "Points Earned", data: monthlyData.map(item => pointsEarned(item)) },
    ],
    [monthlyData]
  );

  const monthOptions = useMemo(
    () => ({
      chart: { fontFamily: "inherit", toolbar: { show: false } },
      colors: ["#2563eb"],
      dataLabels: { enabled: false },
      grid: { borderColor: "#edf0f5", strokeDashArray: 4 },
      legend: { horizontalAlign: "left", position: "top" },
      plotOptions: { bar: { borderRadius: 5, columnWidth: "45%" } },
      stroke: { curve: "smooth", width: 3 },
      xaxis: {
        categories: monthLabels,
        labels: { rotate: -35, style: { colors: "#64748b", fontSize: "11px" } },
      },
      yaxis: {
        labels: { formatter: value => fmt.format(num(value)) },
      },
      tooltip: { y: { formatter: value => fmt.format(num(value)) } },
    }),
    [monthLabels]
  );

  return (
    <>
      <SecoundaryHeader text={title} sideHide={true} onBack={() => history.goBack()} />
      <main style={styles.page}>
        <div style={styles.shell}>
          {loading ? (
            <div style={styles.loader}>
              <div id="quiz-loader"></div>
            </div>
          ) : (
            <>
              {errorMessage && <div style={styles.alert}>{errorMessage}</div>}

              <section style={styles.card}>
                <div style={styles.titleRow}>
                  <div>
                    <h2 style={styles.h2}>Month Wise Analytics</h2>
                    <p style={styles.hint}>Points earned month wise.</p>
                  </div>
                </div>

                {!monthlyData.length ? (
                  <div style={{ ...styles.hint, padding: "34px 0", textAlign: "center" }}>
                    No month wise data found.
                  </div>
                ) : (
                  <Chart options={monthOptions} series={monthSeries} type="line" height={360} />
                )}
              </section>

              {monthlyData.length ? (
                <section style={styles.card}>
                  <div style={styles.titleRow}>
                    <div>
                      <h2 style={styles.h2}>Month Wise Data</h2>
                      <p style={styles.hint}>Direct points earned data for this scheme.</p>
                    </div>
                  </div>
                  <div style={styles.monthGrid}>
                    {monthlyData.map(item => (
                      <div style={styles.monthCard} key={`${item.Year}-${item.MonthNumber}`}>
                        <div style={styles.monthName}>{item.Month} {item.Year}</div>
                        <div style={styles.metricRow}>
                          <span style={styles.metricLabel}>Earned</span>
                          <span style={styles.metricValue}>{fmt.format(pointsEarned(item))}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}
            </>
          )}
        </div>
      </main>
    </>
  );
};

export default SchemeMonthWiseAnalytics;
